import React, { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { Button } from '@mui/material';
import MeetingService from 'services/MeetingService';
import Modal from 'components/shared/Modal';
import { ModalProps } from 'store/types/ComponentProps';
import { MeetingView } from 'store/types/Meeting';
import { defaultSnackbarErrorProps } from 'util/layout';
import { defaultFormProps } from 'util/form';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import PasswordFormItem from 'components/shared/PasswordFormItem';

interface DeleteMeetingModalProps extends ModalProps {
  onSubmit: () => void;
  data?: MeetingView;
}

interface ValidateMeetingFormValues {
  password: string;
}

const DeleteMeetingModal: React.FunctionComponent<DeleteMeetingModalProps> = ({ onClose, open, onSubmit, data }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const ValidateMeetingForm = useForm<ValidateMeetingFormValues>({
    ...defaultFormProps,
    defaultValues: {
      password: '',
    },
  });

  const { formState, handleSubmit, reset } = ValidateMeetingForm;

  const submitButtonDisabled = !formState.isValid || !formState.isDirty || loading;

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const handlePasswordSubmit = useCallback(
    async (formValues: ValidateMeetingFormValues) => {
      if (!data) return;

      setLoading(true);
      try {
        const isValid = await MeetingService.validateMeetingPassword(data.id, formValues.password);

        if (!isValid) {
          enqueueSnackbar('Invalid password', { variant: 'error' });
          return;
        }

        await MeetingService.deleteMeeting(data.id);

        onSubmit();
        reset();
        enqueueSnackbar(`Deleted ${data.title}`, { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(`Failed to delete ${data.title}`, defaultSnackbarErrorProps);
        console.error('Error deleting meeting:', error);
      } finally {
        setLoading(false);
      }
    },
    [data, enqueueSnackbar, reset, onSubmit]
  );

  return (
    <Modal
      title={'Delete Meeting'}
      open={open}
      maxWidth={'xs'}
      loading={loading}
      onClose={handleClose}
      onSubmit={handleSubmit(handlePasswordSubmit)}
      actions={
        <>
          <Button color={'secondary'} variant={'outlined'} disabled={loading} onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" color={'error'} variant={'contained'} disabled={submitButtonDisabled}>
            Delete
          </Button>
        </>
      }
    >
      <FormProvider {...ValidateMeetingForm}>
        <GridContainer>
          <GridItem>
            <p>Are you sure you want to delete the meeting?</p>
          </GridItem>
          <GridItem>
            <PasswordFormItem />
          </GridItem>
        </GridContainer>
      </FormProvider>
    </Modal>
  );
};

export default DeleteMeetingModal;
